import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, withModifiers as _withModifiers, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-3955a672"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "kam-forms" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "switch-label" }, "Toon gesloten formulieren:", -1))
const _hoisted_3 = {
  key: 0,
  class: "detail-cell"
}
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { key: 1 }
const _hoisted_6 = { key: 2 }
const _hoisted_7 = { key: 0 }
const _hoisted_8 = {
  key: 0,
  class: "detail-cell"
}
const _hoisted_9 = { key: 0 }
const _hoisted_10 = {
  key: 8,
  class: "action-cell"
}
const _hoisted_11 = { key: 1 }

import { computed, h, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ls } from '@/ls'
import Column, { ColumnFilterDropdownProps, ColumnFilterItem } from '@/types/column'
import TableSettings from '@/types/tableSettings'
import axios from 'axios'
import type { AxiosResponse } from 'axios'
import { Button, Input, notification, Tree } from 'ant-design-vue'
import { DownloadOutlined } from '@ant-design/icons-vue'
import PageTitleBar from '@/components/PageTitleBar.vue'
import ColumnSettings from '@/components/ColumnSettings.vue'
import { KAMFormStatusLabelColors } from '@/helpers/kamFormStates'
import kamStore from '@/stores/kam.ts'
import { forms as registryForms } from '@/views/kam/formResigstry'
import { dateTimeOptions } from '@/helpers/globalVariables'
import FilterBox from '@/components/FilterBox.vue'

type ServerListItem = { id: number | string; name: string; children?: ServerListItem[] }
type ServerLists = { projects?: ServerListItem[]; forms?: ServerListItem[]; status?: ServerListItem[]; details?: ServerListItem[]; created_by_users?: ServerListItem[]; project_leaders?: ServerListItem[] } | null
type FilterValue = string | number
type ActiveFilters = Record<string, FilterValue[]>
type TreeFilterNode = { title: string | number; key: string; children?: TreeFilterNode[] }
type TreeCheckedKey = string | number | boolean
type TreeCheckedKeys = TreeCheckedKey[] | { checked: TreeCheckedKey[] }

interface KAMRecord {
  id: number
  form?: { reference?: string, name?: string } | null
  project?: { reference?: string } | null
  details?: { company?: string | null, contact_person?: string | null } | null
  project_leader?: { id?: number, name?: string, email?: string } | null
  created_by_user?: { id?: number, name?: string, company?: string | null } | null
  created_by?: { id?: number, name?: string, company?: string | null } | null
}


export default /*#__PURE__*/_defineComponent({
  __name: 'KAMForms',
  setup(__props) {

const router = useRouter()
const tableSettings = ref<TableSettings>(ls.get('KAMFormsTableSettings') || {
  pagination: { current: 1, pageSize: 25 },
  showSizeChanger: true,
  pageSizeOptions: ['25', '50', '100'],
  activeFilters: null,
  sort: null,
  search: null,
  archived: false
})

const treeFilterMode: Column['filterMode'] = 'tree'

const columnDefinitions: Column[] = [
  { title: '#', key: 'id', dataIndex: 'id', sorter: true },
  { title: 'Project', key: 'project.id', dataIndex: 'project.id', filters: [] },
  { title: 'Formulier', key: 'form', dataIndex: 'form' },
  { title: 'Bedrijf/contact', key: 'details', dataIndex: 'details', filters: [], filterMode: treeFilterMode, filterDropdown: createTreeFilterDropdown('details'), width: 220 },
  { title: 'Projectleider', key: 'project_leader.id', dataIndex: 'project_leader.id', filters: [], width: 180 },
  { title: 'Aangemaakt door', key: 'created_by_user.id', dataIndex: 'created_by_user.id', filters: [], filterMode: treeFilterMode, filterDropdown: createTreeFilterDropdown('created_by_user.id'), width: 200 },
  { title: 'Gemeld op', key: 'created_at', dataIndex: 'created_at', sorter: true },
  { title: 'Status', key: 'status', dataIndex: 'status', filters: [], width: 200, align: 'center' },
  { title: 'Acties', key: 'download', dataIndex: 'download', width: 80, align: 'center' }
]

const columns = ref<Column[]>(columnDefinitions)

const hiddenColumns = ref<string[]>([])

// types for server lists returned in kamStore().kamForms
const treeFilterSearchTerms = ref<Record<string, string>>({})

// Extract the server-provided lists from the store in a typed, defensive way.
const serverLists = computed<ServerLists>(() => {
  const payload = kamStore().kamForms as unknown
  if (!payload) return null
  const p = payload as Record<string, unknown>
  if ('filters' in p && p.filters && typeof p.filters === 'object') return (p.filters as unknown) as ServerLists
  if ('lists' in p && p.lists && typeof p.lists === 'object') return (p.lists as unknown) as ServerLists
  // direct shape
  if ('projects' in p || 'forms' in p || 'status' in p) return p as unknown as ServerLists
  return null
})

// Individual computed filter option arrays derived from the server lists (clean, no mutation)
const projectFilterOptions = computed(() => {
  const lists = serverLists.value
  if (!lists || !Array.isArray(lists.projects)) return [] as { text: string; value: number | string }[]
  return lists.projects.map(item => ({ text: item.name, value: item.id }))
})

// Use the form registry as the authoritative source for available form templates
const formFilterOptions = computed(() => {
  if (!registryForms || !Array.isArray(registryForms)) return [] as { text: string; value: number | string }[]
  return registryForms.map(item => ({ text: item.name ?? item.name, value: item.reference }))
})

const statusFilterOptions = computed(() => {
  const lists = serverLists.value
  return lists?.status.map(item => ({ text: item.name, value: item.id }))
})

const createdByFilterOptions = computed(() => {
  const lists = serverLists.value
  if (!lists || !Array.isArray(lists.created_by_users)) return [] as ColumnFilterItem[]
  return mapServerListToFilterItems(lists.created_by_users)
})

const detailsFilterOptions = computed(() => {
  const lists = serverLists.value
  if (!lists || !Array.isArray(lists.details)) return [] as ColumnFilterItem[]
  return mapServerListToFilterItems(lists.details)
})

const projectLeaderFilterOptions = computed(() => {
  const lists = serverLists.value
  if (!lists || !Array.isArray(lists.project_leaders)) return [] as { text: string; value: number | string }[]
  return lists.project_leaders.map(item => ({ text: item.name, value: item.id }))
})

function mapServerListToFilterItems (items: ServerListItem[]): ColumnFilterItem[] {
  return items.map(item => ({
    text: item.name,
    value: item.id,
    children: item.children ? mapServerListToFilterItems(item.children) : undefined
  }))
}

function createTreeFilterDropdown (columnKey: string): NonNullable<Column['filterDropdown']> {
  return ({ setSelectedKeys, selectedKeys, confirm, clearFilters, filters = [] }: ColumnFilterDropdownProps) => {
    const searchTerm = treeFilterSearchTerms.value[columnKey] ?? ''
    const filteredTreeData = filterTreeData(filters, searchTerm)

    return h('div', { class: 'kam-tree-filter-dropdown', onClick: (event: MouseEvent) => event.stopPropagation() }, [
      h('div', { class: 'kam-tree-filter-header' }, [
        h(Input, {
          value: searchTerm,
          placeholder: 'Zoeken in filters',
          allowClear: true,
          class: 'kam-tree-filter-search',
          onChange: (event: Event) => {
            treeFilterSearchTerms.value[columnKey] = (event.target as HTMLInputElement).value
          }
        })
      ]),
      h('div', { class: 'kam-tree-filter-tree' }, [
        filteredTreeData.length
          ? h(Tree, {
            checkable: true,
            selectable: false,
            blockNode: true,
            multiple: true,
            checkedKeys: selectedKeys.map(String),
            selectedKeys: selectedKeys.map(String),
            treeData: filteredTreeData,
            expandedKeys: getTreeNodeKeys(filteredTreeData),
            autoExpandParent: true,
            showIcon: false,
            onCheck: (checkedKeys: TreeCheckedKeys) => {
              const keys = Array.isArray(checkedKeys) ? checkedKeys : checkedKeys.checked
              setSelectedKeys(keys)
            }
          })
          : h('div', { class: 'kam-tree-filter-empty' }, 'Geen filters gevonden.')
      ]),
      h('div', { class: 'kam-tree-filter-actions' }, [
        h(Button, {
          size: 'small',
          type: 'link',
          onClick: () => {
            treeFilterSearchTerms.value[columnKey] = ''
            if (clearFilters) clearFilters()
            confirm()
          }
        }, { default: () => 'Reset' }),
        h(Button, {
          size: 'small',
          type: 'primary',
          onClick: () => confirm()
        }, { default: () => 'OK' })
      ])
    ])
  }
}

function filterTreeData (items: ColumnFilterItem[], input: string): TreeFilterNode[] {
  const searchTerm = input.trim().toLowerCase()

  return items.flatMap((item) => {
    const children = item.children ? filterTreeData(item.children, input) : []
    const itemMatches = !searchTerm || String(item.text).toLowerCase().includes(searchTerm)

    if (!itemMatches && !children.length) return []

    return [{
      title: item.text,
      key: String(item.value),
      children: itemMatches ? mapFilterItemsToTreeNodes(item.children ?? []) : children
    }]
  })
}

function mapFilterItemsToTreeNodes (items: ColumnFilterItem[]): TreeFilterNode[] {
  return items.map(item => ({
    title: item.text,
    key: String(item.value),
    children: item.children ? mapFilterItemsToTreeNodes(item.children) : undefined
  }))
}

function getTreeNodeKeys (items: TreeFilterNode[]): string[] {
  return items.flatMap(item => [item.key, ...(item.children ? getTreeNodeKeys(item.children) : [])])
}

function normalizeActiveFilters (activeFilters: ActiveFilters | null | undefined): ActiveFilters | null {
  if (!activeFilters) return null

  const normalizedFilters: ActiveFilters = {}
  Object.keys(activeFilters).forEach((key) => {
    const values = normalizeFilterValues(key, activeFilters[key] ?? [])
    if (values.length) {
      normalizedFilters[key] = values
    }
  })

  return Object.keys(normalizedFilters).length ? normalizedFilters : null
}

function normalizeFilterValues (key: string, values: FilterValue[]): FilterValue[] {
  if (!values.length) return []

  const treeOptions = getTreeFilterOptions(key)
  const normalizedValues: FilterValue[] = []
  const seenValues = new Set<string>()

  values.forEach((value) => {
    const expandedValues = treeOptions.length ? expandTreeFilterValue(treeOptions, value) : [value]

    expandedValues.forEach((expandedValue) => {
      if (isTreeGroupValue(key, expandedValue)) return

      const seenKey = String(expandedValue)
      if (!seenValues.has(seenKey)) {
        normalizedValues.push(expandedValue)
        seenValues.add(seenKey)
      }
    })
  })

  return normalizedValues
}

function getTreeFilterOptions (key: string): ColumnFilterItem[] {
  if (key === 'created_by_user.id') return createdByFilterOptions.value
  if (key === 'details') return detailsFilterOptions.value
  return []
}

function expandTreeFilterValue (items: ColumnFilterItem[], value: FilterValue): FilterValue[] {
  const item = findTreeFilterItem(items, value)
  if (!item) return [value]
  return item.children?.length ? getLeafFilterValues(item.children) : [item.value as FilterValue]
}

function findTreeFilterItem (items: ColumnFilterItem[], value: FilterValue): ColumnFilterItem | null {
  for (const item of items) {
    if (String(item.value) === String(value)) {
      return item
    }

    if (item.children?.length) {
      const childItem = findTreeFilterItem(item.children, value)
      if (childItem) return childItem
    }
  }

  return null
}

function getLeafFilterValues (items: ColumnFilterItem[]): FilterValue[] {
  return items.flatMap((item) => {
    if (item.children?.length) return getLeafFilterValues(item.children)
    return [item.value as FilterValue]
  })
}

function isTreeGroupValue (key: string, value: FilterValue): boolean {
  return (key === 'created_by_user.id' || key === 'details') && typeof value === 'string' && value.startsWith('company:')
}

tableSettings.value.activeFilters = normalizeActiveFilters(tableSettings.value.activeFilters)

const filteredColumns = computed(() => {
  const active = tableSettings.value.activeFilters as Record<string, (string|number)[]> | null
  return columns.value
    .filter(c => !hiddenColumns.value.includes(c.key))
    .map((c) => {
      // shallow clone to avoid mutating source
      const col: Column & { filters?: ColumnFilterItem[] } = Object.assign({}, c)

      if (col.key === 'project.id') col.filters = projectFilterOptions.value
      if (col.key === 'form') col.filters = formFilterOptions.value
      if (col.key === 'details') col.filters = detailsFilterOptions.value
      if (col.key === 'status') col.filters = statusFilterOptions.value
      if (col.key === 'created_by_user.id') col.filters = createdByFilterOptions.value
      if (col.key === 'project_leader.id') col.filters = projectLeaderFilterOptions.value

      // Set active filteredValue from persistent tableSettings (do not mutate source columns)
      if (active && col.key in active) {
        col.filteredValue = active[col.key]
      } else {
        col.filteredValue = null
      }

      // Apply sort state from tableSettings (if present)
      if (tableSettings.value.sort && col.key === tableSettings.value.sort.columnKey) {
        col.defaultSortOrder = tableSettings.value.sort.order
        col.sortOrder = tableSettings.value.sort.order
      } else {
        col.sortOrder = false
      }

      return col
    })
})

function onTableSettingsChange () : void {
  // persist using the same key we used during initialization
  ls.set('KAMFormsTableSettings', tableSettings.value)

  // optional persistent storage on server
  axios.post(process.env.VUE_APP_API_URL + '/users/constructionSiteApp/settings', {
    settingName: 'KAMFormsTableSettings',
    settingValue: tableSettings.value
  })
}

onMounted(() => {
  // Fetch the list (filters will be derived from the store payload)
  kamStore().getKAMForms(tableSettings.value)
  onTableSettingsChange()
})

// Keep tableSettings <-> columns in sync and persist when tableSettings changes (deep watch)
watch(() => tableSettings.value, () => {
  onTableSettingsChange()
}, { deep: true })

function onCustomRowClick (record:{ id:number, project?: { reference: string }|null, form?: { reference:string }|null }) : { onClick: () => void } {
  return {
    onClick: () => {
      const projectReference = record.project?.reference ?? ''
      const formReference = record.form?.reference ?? ''

      router.push({
        name: 'KAMFormEdit',
        params: { kamFormId: record.id, formReference: formReference, projectReference: projectReference }
      }).catch((err) => { if (err) console.debug('Navigation error when pushing KAMFormEdit:', err) })
    }
  }
}

function archivedChanged () : void {
  resetPagination()
  if (tableSettings.value.activeFilters?.status) {
    delete tableSettings.value.activeFilters.status
  }
  kamStore().getKAMForms(tableSettings.value)
}

function handleTableChange (pagination:{ current:number, pageSize:number }, filters:{[key: string]: number[]|string[]}, sort:{ columnKey:string, order:string }) : void {
  // Normalize empty filter objects by removing their keys
  if (filters && Object.keys(filters).length) {
    Object.keys(filters).forEach((key) => {
      if (!filters[key] || !filters[key].length) {
        delete filters[key]
      }
    })
  }

  // Set pagination, filters and sorting on tableSettings.
  tableSettings.value.pagination = pagination
  tableSettings.value.activeFilters = normalizeActiveFilters(filters as ActiveFilters)
  tableSettings.value.sort = sort.order ? sort : null

  // Get list based on filters and search term.
  kamStore().getKAMForms(tableSettings.value)
}

function handleSearchChange (searchTerm:string) : void {
  tableSettings.value.search = searchTerm
  resetPagination()
  kamStore().getKAMForms(tableSettings.value)
}

function updateFilters (activeFilters:{[key: string]: number[]|string[]}) : void {
  tableSettings.value.activeFilters = normalizeActiveFilters(activeFilters as ActiveFilters)
  resetPagination()
  kamStore().getKAMForms(tableSettings.value)
}

function resetPagination () : void {
  tableSettings.value.pagination.current = 1
}

const pagination = computed(() => {
  return {
    current: tableSettings.value.pagination.current,
    defaultPageSize: tableSettings.value.pagination.pageSize,
    pageSize: parseInt(kamStore().kamForms.meta.per_page),
    total: kamStore().kamForms.meta.total,
    showTotal: (total:number, range:number[]) => `KAM-formulier ${range[0]} t/m ${range[1]} van de ${total}`,
    showSizeChanger: tableSettings.value.showSizeChanger,
    pageSizeOptions: tableSettings.value.pageSizeOptions
  }
})

// per-row downloading state
const downloading = ref<Record<number, boolean>>({})

function filenamePart (value: string): string {
  return value
    .replace(/[^a-z0-9_-]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase() || 'formulier'
}

// Determine if a KAM form can be downloaded: status present and greater than 1
function canDownload (record: KAMRecord | Record<string, unknown> | null | undefined) : boolean {
  if (!record) return false
  // defensive: support numeric or string status values without using `any`
  const rec = record as Record<string, unknown>
  const s = rec.status
  if (s === undefined || s === null) return false
  if (typeof s === 'number') return (s as number) > 1
  if (typeof s === 'string') {
    const n = Number(s)
    return !Number.isNaN(n) && n > 1
  }
  return false
}

async function downloadKAMForm (record: KAMRecord) : Promise<void> {
  const id = record.id
  try {
    downloading.value[id] = true
    const resp: AxiosResponse<ArrayBuffer> = await axios.get(process.env.VUE_APP_API_URL + '/kamForms/' + id + '/export', { responseType: 'arraybuffer' })
    const data = resp.data as ArrayBuffer
    const blob = new Blob([data], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    const formName = record.form?.name ?? 'formulier'
    const projectRef = record.project?.reference ?? 'project'
    const filename = `${filenamePart(formName)}_${projectRef}_${id}.pdf`
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    notification.success({ message: 'Download gestart.', description: 'De PDF export is gestart. Check je downloadmap.', duration: 4 })
  } catch (e) {
    console.error(e)
    notification.error({ message: 'Download mislukt.', description: 'Het downloaden van het KAM-formulier is mislukt. Probeer het later nog een keer.', duration: 0 })
  } finally {
    downloading.value[id] = false
  }
}

return (_ctx: any,_cache: any) => {
  const _component_a_button = _resolveComponent("a-button")!
  const _component_a_switch = _resolveComponent("a-switch")!
  const _component_a_input_search = _resolveComponent("a-input-search")!
  const _component_a_tag = _resolveComponent("a-tag")!
  const _component_a_tooltip = _resolveComponent("a-tooltip")!
  const _component_a_table = _resolveComponent("a-table")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(PageTitleBar, null, {
      title: _withCtx(() => [
        _createTextVNode("KAM formulieren.")
      ]),
      buttons: _withCtx(() => [
        _createVNode(_component_a_button, {
          type: "primary",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).push({ name: 'KAMFormCreate' })))
        }, {
          default: _withCtx(() => [
            _createTextVNode("Toevoegen")
          ]),
          _: 1
        }),
        _createVNode(ColumnSettings, {
          columns: columns.value,
          storageName: "hiddenKAMFormColumns",
          onHiddenColumnsChanged: _cache[1] || (_cache[1] = ($event: any) => (hiddenColumns.value = $event))
        }, null, 8, ["columns"]),
        _createVNode(_component_a_switch, {
          checked: tableSettings.value.archived,
          "onUpdate:checked": _cache[2] || (_cache[2] = ($event: any) => ((tableSettings.value.archived) = $event)),
          onChange: archivedChanged
        }, null, 8, ["checked"]),
        _createTextVNode(),
        _hoisted_2
      ]),
      search: _withCtx(() => [
        _createVNode(_component_a_input_search, {
          placeholder: "Zoeken..",
          style: {"width":"100%"},
          value: tableSettings.value.search,
          "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((tableSettings.value.search) = $event)),
          onSearch: handleSearchChange,
          "enter-button": "",
          "allow-clear": ""
        }, null, 8, ["value"])
      ]),
      _: 1
    }),
    _createVNode(FilterBox, {
      view: "kams",
      "active-filters": tableSettings.value.activeFilters,
      columns: _unref(filteredColumns),
      onUpdate: updateFilters
    }, null, 8, ["active-filters", "columns"]),
    _createVNode(_component_a_table, {
      columns: _unref(filteredColumns),
      "data-source": _unref(kamStore)().kamForms.data,
      loading: _unref(kamStore)().loading.list,
      "row-class-name": "clickable-row",
      rowKey: "id",
      customRow: onCustomRowClick,
      scroll: { x: true },
      pagination: _unref(pagination),
      onChange: handleTableChange
    }, {
      bodyCell: _withCtx(({ column, record }) => [
        (column.key === 'id')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createTextVNode(_toDisplayString(record.id), 1)
            ], 64))
          : _createCommentVNode("", true),
        (column.key === 'project.id')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _createTextVNode(_toDisplayString(record.project ? record.project.name + ' (' + record.project.reference + ')' : '-'), 1)
            ], 64))
          : _createCommentVNode("", true),
        (column.key === 'form')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createTextVNode(_toDisplayString(record.form.name), 1)
            ], 64))
          : _createCommentVNode("", true),
        (column.key === 'details')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
              (record.details && (record.details.company || record.details.contact_person))
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    (record.details.company)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(record.details.company), 1))
                      : (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(record.details.contact_person), 1)),
                    (record.details.company && record.details.contact_person)
                      ? (_openBlock(), _createElementBlock("small", _hoisted_6, _toDisplayString(record.details.contact_person), 1))
                      : _createCommentVNode("", true)
                  ]))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createTextVNode("-")
                  ], 64))
            ], 64))
          : _createCommentVNode("", true),
        (column.key === 'project_leader.id')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
              (record.project_leader && record.project_leader.name)
                ? (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(record.project_leader.name), 1))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createTextVNode("-")
                  ], 64))
            ], 64))
          : _createCommentVNode("", true),
        (column.key === 'created_by_user.id')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 5 }, [
              (record.created_by_user || record.created_by)
                ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                    _createElementVNode("span", null, _toDisplayString((record.created_by_user || record.created_by).name), 1),
                    ((record.created_by_user || record.created_by).company)
                      ? (_openBlock(), _createElementBlock("small", _hoisted_9, _toDisplayString((record.created_by_user || record.created_by).company), 1))
                      : _createCommentVNode("", true)
                  ]))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createTextVNode("-")
                  ], 64))
            ], 64))
          : _createCommentVNode("", true),
        (column.key === 'created_at')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 6 }, [
              _createTextVNode(_toDisplayString(record.created_at ? new Date(record.created_at).toLocaleString('nl-NL', _unref(dateTimeOptions)) : '-'), 1)
            ], 64))
          : _createCommentVNode("", true),
        (column.key === 'status')
          ? (_openBlock(), _createBlock(_component_a_tag, {
              key: 7,
              class: "status-tag",
              color: _unref(KAMFormStatusLabelColors)[record.status],
              title: record.status_label
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(record.status_label), 1)
              ]),
              _: 2
            }, 1032, ["color", "title"]))
          : _createCommentVNode("", true),
        (column.key === 'download')
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
              (canDownload(record))
                ? (_openBlock(), _createBlock(_component_a_tooltip, {
                    key: 0,
                    placement: "top",
                    title: "Download PDF"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_a_button, {
                        size: "middle",
                        type: "ghost",
                        onClick: _withModifiers(($event: any) => (downloadKAMForm(record)), ["stop"]),
                        loading: (downloading.value[record.id] ?? false),
                        "aria-label": "Download PDF"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(DownloadOutlined))
                        ]),
                        _: 2
                      }, 1032, ["onClick", "loading"])
                    ]),
                    _: 2
                  }, 1024))
                : (_openBlock(), _createElementBlock("span", _hoisted_11, "-"))
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["columns", "data-source", "loading", "pagination"])
  ]))
}
}

})